import { List } from "immutable";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import { OUTLINE_VIEW } from "../../constants/identifiers";
import tableRowTypes from "../../constants/tableRowTypes";
import inspectorSelectors from "../../features/inspector/selectors";
import { appStateSelectors } from "../../modules/appState";
import { characterWidthMapSelectors } from "../../modules/characterWidthMap";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { narrativeSelectors } from "../../modules/narrative";
import { tagSelectors } from "../../modules/tags";
import { uiStateSelectors } from "../../modules/uiState";
import itemCardSelectors from "../itemCard/selectors";
import { findCardWithId } from "../narrativeView/utils";
import tableViewSelectors from "../sharedSelectors/tableViewSelectors";
import workViewSelectors from "../workView/selectors";

/**
 *
 * @param {Immutable.List} items
 * @param {Immutable.Set} expandedItemsSet
 * @param {TreeRecord} treeNode
 * @param {number} indentationLevel
 */
const getRowsForNarrativeTree = (items, expandedItemsSet, treeNode, indentationLevel) => {
  // get all items with the requested super
  // const thisLevel = treeNode.children.map(c => items.find(o => o.id === c || o.id === c.id));
  //  items.filter(o => o.super === superId).sort(sortFunction);
  // Now work out if we need to get any nested children
  let allLevels = List();
  // Iterate through each item at this level...
  treeNode.children.forEach(c => {
    if (c.children) {
      // This item is expanded with children, so make a recursive call to grab their children also
      const children = getRowsForNarrativeTree(items, expandedItemsSet, c, indentationLevel + 1);

      const itemWithChildren = items.find(o => o.id === c.id);
      if (itemWithChildren) {
        const obj = itemWithChildren.merge({
          children: children,
          superDepth: indentationLevel,
          super: treeNode.id
        });

        // And now we want to insert them after this item.
        // const insertionIndex = allLevels.findIndex(a => a.id === c.id);
        allLevels = allLevels.push(obj);
      }

      if (expandedItemsSet.has(c.id)) {
        allLevels = allLevels.push(children).flatten();
      }
    } else {
      const obj = items
        .find(o => o.id === c || o.id === c.id)
        .merge({
          superDepth: indentationLevel,
          super: treeNode.id
        });
      allLevels = allLevels.push(obj);
    }
  });
  // Return the items at this level
  return allLevels;
};

const getRowsWithItems = createCachedSelector(
  // fetch parameters for last calculate function
  (_state, _viewId, items) => items,
  appStateSelectors.getViewExpandedItems,
  narrativeSelectors.getNarrativeTree,
  // Calculate result
  (items, expandedItems, narrativeTree) => {
    // Get all objects that match the desired type
    return getRowsForNarrativeTree(items, expandedItems.toSet(), narrativeTree, 0);
  }
)(workViewSelectors.getViewId);

const getRowsWithItemsForMobile = createCachedSelector(
  // fetch parameters for last calculate function
  (_state, _viewId, items) => items,
  appStateSelectors.getViewExpandedItemsForMobile,
  narrativeSelectors.getNarrativeTree,
  // Calculate result
  (items, expandedItems, narrativeTree) => {
    // Get all objects that match the desired type
    return getRowsForNarrativeTree(items, expandedItems.toSet(), narrativeTree, 0);
  }
)(workViewSelectors.getViewId);

// Sort Events - Makes sure selected events are in correct order...
const getRows = createCachedSelector(
  // inputs
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getItemsInGroupForView,
  // outputs
  getRowsWithItems
)(workViewSelectors.getViewId);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getRowsForMobile = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getItemsInGroupForViewForMobile,
  getRowsWithItemsForMobile
)(workViewSelectors.getViewId);

const getSelectedRows = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getSelectedItemsInGroupForView,
  getRowsWithItems
)(workViewSelectors.getViewId);

const getOutlineViewAllowedColumns = (state, viewId) =>
  tableViewSelectors.getAllowedColumns(state, viewId, typeSelectors.getOrderedNarrativeTypes(state), false);
const getOutlineViewAvailableColumns = (state, viewId) =>
  tableViewSelectors.getAvailableColumns(state, viewId, typeSelectors.getOrderedNarrativeTypes(state), false);

const getOutlineViewOptionsForColumns = (state, rowId, columnId) => {
  if (rowId !== tableRowTypes.EMPTY && (columnId == "blocks" || columnId == "blockedBy")) {
    return inspectorSelectors.getDependencyOptions(state, List([rowId]));
  }
  return tableViewSelectors.getOptionsForColumn(state, typeSelectors.getOrderedNarrativeTypes(state), columnId, rowId);
};

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getOutlineViewColumns = createCachedSelector(
  getOutlineViewAllowedColumns,
  appStateSelectors.getOutlineOptionsForView,
  appStateSelectors.getFontSize,
  // calculate result: (list of entities matching the type)
  tableViewSelectors.getColumns
)(workViewSelectors.getViewId);

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getOutlineViewData = (state, viewId) =>
  tableViewSelectors.getTableData(
    state,
    viewId,
    getRows(state, viewId),
    getOutlineViewColumns(state, viewId),
    "narrativeNestedTitleMetrics",
    true,
    true
  );

const getOutlineViewSelectedData = (state, viewId) =>
  tableViewSelectors.getTableData(
    state,
    viewId,
    getSelectedRows(state, viewId),
    getOutlineViewColumns(state, viewId),
    "narrativeNestedTitleMetrics",
    true,
    true
  );

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getOutlineViewSortedItemIds = (state, viewId) =>
  tableViewSelectors.getSortedRowIds(state, viewId, getRows(state, viewId));
const getOutlineViewSortedItemIdsForMobile = (state, viewId, viewWidth, showDragHandles) =>
  tableViewSelectors.getSortedRowIds(state, viewId, getRowsForMobile(state, viewId, viewWidth, showDragHandles));

const getOutlineViewDefaultType = state => definitionSelectors.getDefaultNarrativeDocumentType(state);

const getOutlineViewDataForItem = createSelector(
  // fetch parameters for last calculate function
  (_state, viewId, _displayData, _itemId, _itemsById, _typesById) => viewId,
  (_state, _viewId, displayData, _itemId, _itemsById, _typesById) => displayData,
  (_state, _viewId, _displayData, itemId, _itemsById, _typesById) => itemId,
  (state, viewId, _displayData, _itemId, _itemsById, _typesById) =>
    workViewSelectors.getDisplayItemsForViewType(state, OUTLINE_VIEW, viewId),
  getOutlineViewColumns,
  (_state, _viewId, _displayData, _itemId, itemsById, _typesById) => itemsById,
  (_state, _viewId, _displayData, _itemId, _itemsById, typesById) => typesById,
  state => definitionSelectors.getEnabledDefinitions(state).colors.byId,
  state => definitionSelectors.getReferencesById(state),
  state => definitionSelectors.getPropertiesById(state),
  state => state.data.relationships.byId,
  state => state.data.dependencies.byId,
  appStateSelectors.getViewExpandedItems,
  state => itemSelectors.getItemIndexesInTreeOrderForAllTypes(state),
  tagSelectors.getTags,
  appStateSelectors.getFontSize,
  characterWidthMapSelectors.getCharacterWidthMap,
  // and calculate
  (
    viewId,
    displayData,
    itemId,
    displayItems,
    columns,
    itemsById,
    typesById,
    colorsById,
    referencesById,
    propertiesById,
    relationshipsById,
    dependenciesById,
    expandedItems,
    globalItemIndexes,
    tagsById,
    fontSize,
    widthMap
  ) => {
    // See if there is already a row for this item.
    const row = displayData.rows ? findCardWithId(displayData.rows, itemId) : null;
    if (row) {
      return row;
    }
    // Otherwise, create a row
    const displayItem = displayItems.find(obj => obj.id === itemId);
    const rowRecord = tableViewSelectors.getIndividualTableRow(
      viewId,
      displayItem,
      0,
      columns,
      itemsById,
      typesById,
      colorsById,
      referencesById,
      propertiesById,
      relationshipsById,
      dependenciesById,
      expandedItems,
      "nestedTitleMetrics",
      false,
      null,
      null,
      null,
      true,
      globalItemIndexes,
      fontSize,
      widthMap,
      tagsById
    );
    return rowRecord;
  }
);

// ==========================
// Mobile Item Card Selectors
// ==========================

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {number} maxWidth
 * @param {boolean} showDragHandles
 */
const getItemCardListRows = createCachedSelector(
  // fetch parameters for last calculate function
  workViewSelectors.getItemsInGroupForViewForMobile,
  appStateSelectors.getViewExpandedItemsForMobile,
  narrativeSelectors.getNarrativeTree,
  // Calculate result
  (items, expandedItems, narrativeTree) => {
    // Get all objects that match the desired type
    return getRowsForNarrativeTree(items, expandedItems.toSet(), narrativeTree, 0);
  }
)(workViewSelectors.getViewId);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {number} maxWidth
 */
const getItemCardTableData = createCachedSelector(
  // fetch parameters for last calculate function
  state => state.data.items.byId,
  getItemCardListRows,
  appStateSelectors.getViewExpandedItemsForMobile,
  () => "itemCardNarrativeNestedTitleMetrics",
  // And caclulate
  itemCardSelectors.getTableData
)(workViewSelectors.getViewId);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {number} maxWidth
 * @param {boolean} showDragHandles
 */
const getItemCardListViewData = createCachedSelector(
  getItemCardTableData,
  uiStateSelectors.getSelectedItems,
  (data, selection) => {
    return data.update("rows", rows =>
      rows.map(r => r.setIn(["itemDisplay", "selected"], selection.includes(r.itemDisplay.id)))
    );
  }
)(workViewSelectors.getViewId);

export default {
  getOutlineViewAvailableColumns,
  getOutlineViewData,
  getOutlineViewOptionsForColumns,
  getOutlineViewSelectedData,
  getOutlineViewSortedItemIds,
  getOutlineViewSortedItemIdsForMobile,
  getOutlineViewDefaultType,
  getItemCardListViewData,
  getOutlineViewDataForItem
};
